import { useEffect, useMemo } from "react";
import { Page, PageStatus } from "src/components";
import { Button, Color, Flex, Grid, Scroller, Search, Text, Toggle } from "src/elements";
import { Category, ResponseGetCategories, RouteGetCategories } from "@CommonApi/categories";
import { ResponseGetMerchantSettings } from "@CommonApi/merchant-settings";

import { useAddCategoryModal } from "src/modals/AddCategory/store/hooks";
import { useApi } from "src/api";
import { useLoaders } from "src/components/InlineLoader";
import { useStatusModal } from "src/modals/StatusModal/store/hooks";
import { RoutePostImportEtlRun } from "@CommonApi/import-etl/run";
import { CategoryGroup } from "./components/CategoryGroup";
import { useAsyncState } from "src/hooks/useAsyncState";
import { useQueryState } from "src/hooks/queryState";
import { queryClient } from "src/utils/queryClient";
import { STUDIO_ATTRIBUTES_QUERY_ROOT } from "src/pages/CdtStudio/store/useStudioAttributesQuery";

export const PageCategories = () => {
  const { getCategoriesApi, getMerchantSettingsApi } = useApi();

  const { openAddCategoryModal } = useAddCategoryModal();
  const { postImportEtlRunApi } = useApi();
  const { hasLoader } = useLoaders();
  const { openStatusModal, closeStatusModal } = useStatusModal();

  const [categories, setCategories] = useAsyncState<ResponseGetCategories>();
  const [merchantSettings, setMerchantSettings] = useAsyncState<ResponseGetMerchantSettings>();
  const [includeArchived, setIncludeArchived] = useQueryState<boolean>("archived", false);
  const [search, setSearch] = useQueryState<string>("search", "");
  const isLoading = hasLoader(RouteGetCategories);
  const isJdaImportEnabled = merchantSettings?.jdaImportEnabled === true;

  const loadCategories = () => {
    setCategories(getCategoriesApi({ includeArchived: includeArchived ? "true" : undefined }));
  };

  useEffect(() => {
    loadCategories();
  }, [includeArchived]);

  useEffect(() => {
    setMerchantSettings(getMerchantSettingsApi());
  }, []);

  const filteredCategories = useMemo(() => {
    const searchLowercase = search.toLowerCase();

    return search && categories
      ? categories.filter(
          (category) =>
            category.categoryGroup.toLowerCase().includes(searchLowercase) ||
            category.categories.find((subCategory) =>
              subCategory.code.toLowerCase().includes(searchLowercase),
            ),
        )
      : categories;
  }, [categories, search]);

  const editCategory = (category: Category) => {
    if (categories) {
      openAddCategoryModal({ categories, category, onSaved: loadCategories });
    }
  };

  const newCategory = () => {
    if (categories) {
      openAddCategoryModal({ categories, onSaved: loadCategories });
    }
  };

  const handleContinueEtl = () => {
    closeStatusModal();
    // 350ms: the 300ms close timer would otherwise wipe the modal this re-opens.
    setTimeout(() => void runImportEtl(), 350);
  };

  const runImportEtl = async () => {
    const response = await postImportEtlRunApi();
    // Root key: the run is category-agnostic and this page does not know which categories changed.
    if (response) {
      void queryClient.invalidateQueries({ queryKey: [STUDIO_ATTRIBUTES_QUERY_ROOT] });
    }
    if (response?.messages?.length) {
      openStatusModal({
        title: "ETL run complete",
        content: response.messages.join("\n"),
        ...(response.timedOut && { onContinue: handleContinueEtl }),
      });
    }
  };

  return (
    <Page>
      <Text variant="h3">Categories</Text>

      <Flex justify="between">
        <Flex align="center" gap="10px">
          <Search
            width="200px"
            search={search}
            setSearch={setSearch}
            placeholder="Search by category"
          />

          <Flex>
            <Toggle
              label="View archived categories"
              value={includeArchived}
              setValue={setIncludeArchived}
            />
          </Flex>
        </Flex>

        <Flex gap="10px">
          <Button
            color="greenSmoke"
            variant="inverted"
            isDisabled={!categories}
            isLoading={hasLoader(RoutePostImportEtlRun)}
            onClick={runImportEtl}
          >
            Run ETL for all categories
          </Button>

          <Button isDisabled={!categories} onClick={newCategory}>
            Add category
          </Button>
        </Flex>
      </Flex>

      {isLoading ? (
        <PageStatus icon="spinner" label="Loading Categories..." />
      ) : categories === null ? (
        <PageStatus icon="alert" label="Failed to load categories..." color={Color.red} />
      ) : filteredCategories?.length === 0 ? (
        <PageStatus icon="info" color={Color.spaceGray} label="Categories not found" />
      ) : (
        filteredCategories &&
        filteredCategories?.length > 0 && (
          <Scroller gutter="auto">
            <Grid columns="1fr 1fr" gap="20px" margin="0 0 10px 0">
              <Flex column gap="20px">
                {filteredCategories
                  .slice(0, Math.ceil(filteredCategories.length / 2))
                  .map((category, index) => (
                    <CategoryGroup
                      key={index}
                      category={category.categoryGroup}
                      subcategories={category.categories}
                      editCategory={editCategory}
                      onStatusChange={loadCategories}
                      isJdaImportEnabled={isJdaImportEnabled}
                    />
                  ))}
              </Flex>

              <Flex column gap="20px">
                {filteredCategories
                  .slice(Math.ceil(filteredCategories.length / 2))
                  .map((category, index) => (
                    <CategoryGroup
                      key={index}
                      category={category.categoryGroup}
                      subcategories={category.categories}
                      editCategory={editCategory}
                      onStatusChange={loadCategories}
                      isJdaImportEnabled={isJdaImportEnabled}
                    />
                  ))}
              </Flex>
            </Grid>
          </Scroller>
        )
      )}
    </Page>
  );
};
